import { Card, CardBody, CardHeader, CardTitle, LoadingContainer } from '@/components/ui';
import { useDashboardEmpresa } from '@/hooks';
import { useHistorialEmpresa } from '@/hooks/useHistorialEmpresa';
import { type Historial } from '@/services/empresas.service';
import { AlertCircle, Award, BarChart2, Clock, ExternalLink, Layers, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

interface ProgresoEmpresaProps {
  nit: string;
}

function formatHours(totalHours: number): string {
  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);
  if (minutes === 0) return `${hours}h 00m`;
  return `${hours}h ${minutes}m`;
}

const NTC_COLORS = [
  '#1e40af', // Deep Blue
  '#047857', // Forest Green
  '#b45309', // Dark Amber
  '#6d28d9', // Deep Violet
  '#be123c', // Crimson Rose
  '#0369a1', // Ocean Blue
  '#334155', // Slate
  '#155e75', // Dark Cyan
  '#7c2d12', // Rust Orange
];

function prepareChartData(historial: Historial[]) {
  return historial
    .sort((a, b) => new Date(a.fechaRegistro).getTime() - new Date(b.fechaRegistro).getTime())
    .map((h, i) => {
      const ntcs = h.ntcsJson ? JSON.parse(h.ntcsJson) : [];
      const ntcData: Record<string, number> = {};
      ntcs.forEach((ntc: any) => {
        ntcData[`ntc_${ntc.ntcId}`] = ntc.porcentaje || 0;
      });

      const label = i === 0 ? 'Diagnóstico' : `Ase. ${i}`;
      const fechaFull = new Date(h.fechaRegistro).toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      return {
        name: `${label} (${fechaFull})`,
        label,
        fecha: fechaFull,
        cumplimiento: h.porcentajeCumplimiento,
        horas: h.horasAsesoria,
        asesoriaId: h.asesoriaId, // use actual asesoría ID, not the historial record id
        mes: h.mes,
        anio: h.anio,
        ...ntcData,
      };
    });
}

type ChartMode = 'separate' | 'combined';

/** Simple non-interactive tooltip — navigation goes through the dot onClick */
function SimpleTooltip({
  active,
  payload,
  label,
  isHoras = false,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
  isHoras?: boolean;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-(--admin-border) rounded-xl shadow-lg p-3 text-[12px] min-w-40 pointer-events-none">
      <div className="font-semibold text-(--admin-text) mb-2 border-b border-(--admin-border) pb-1.5 text-[11px]">{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2 py-0.5">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
          <span className="text-(--admin-text2) text-[11px]">{p.name}</span>
          <span className="ml-auto font-bold text-(--admin-text)">
            {typeof p.value === 'number' ? (isHoras ? formatHours(p.value) : `${p.value.toFixed(1)}%`) : p.value}
          </span>
        </div>
      ))}
      <p className="mt-1.5 pt-1.5 border-t border-(--admin-border) text-[10px] text-(--admin-text3) text-center italic">
        clic en el punto para abrir asesoría
      </p>
    </div>
  );
}

/** Returns activeDot element with navigation onClick — avoids chart-level click issues */
function makeDot(
  chartData: ReturnType<typeof prepareChartData>,
  navigate: ReturnType<typeof useNavigate>,
  nit: string,
  color: string,
  r = 6
) {
  // eslint-disable-next-line react/display-name
  return (props: any) => {
    const { cx, cy, index } = props;
    const point = chartData[index];
    const clickable = !!point?.asesoriaId;
    return (
      <circle
        key={`adot-${index}`}
        cx={cx}
        cy={cy}
        r={r + 2}
        fill={color}
        stroke="white"
        strokeWidth={2}
        style={{ cursor: clickable ? 'pointer' : 'default' }}
        onClick={() => {
          if (clickable) navigate(`/empresas/${nit}/asesorias/${point.asesoriaId}`);
        }}
      />
    );
  };
}

export function ProgresoEmpresa({ nit }: ProgresoEmpresaProps) {
  const navigate = useNavigate();
  const { data: historial, isLoading, error } = useHistorialEmpresa(nit);
  const { data: empresaData } = useDashboardEmpresa(nit);
  const [chartMode, setChartMode] = useState<ChartMode>('separate');

  if (isLoading) return <LoadingContainer text="Cargando progreso de empresa..." />;

  if (error) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error al cargar progreso</h2>
            <p className="text-gray-500">{error}</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!historial || historial.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-12">
            <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Sin historial</h2>
            <p className="text-gray-500">Esta empresa aún no tiene registros de progreso</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  const chartData = prepareChartData(historial);
  const latestData = chartData[chartData.length - 1];
  const firstData = chartData[0];
  const cambioCumplimiento = latestData.cumplimiento - firstData.cumplimiento;

  const META_HORAS = 320;
  const horasAvancePct = (latestData.horas / META_HORAS) * 100;
  const cumplColor = latestData.cumplimiento >= 70 ? 'var(--admin-green)' : latestData.cumplimiento >= 50 ? '#e09b00' : 'var(--admin-red)';

  const ntcKeys =
    empresaData?.ntcs?.map((ntc, i) => ({
      key: `ntc_${ntc.ntcId}`,
      label: ntc.nombre,
      color: NTC_COLORS[i % NTC_COLORS.length],
      ntcId: ntc.ntcId,
    })) ?? [];

  // handleChartClick removed — navigation now handled via makeDot activeDot onClick

  return (
    <div className="space-y-6">
      {/* KPI summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="admin-detail-field" style={{ textAlign: 'center' }}>
          <Clock className="w-5 h-5 mx-auto mb-1" style={{ color: 'var(--admin-blue)' }} />
          <p className="text-[22px] font-bold" style={{ color: 'var(--admin-blue)' }}>
            {horasAvancePct.toFixed(1)}%
          </p>
          <p className="text-[11px]" style={{ color: 'var(--admin-text3)' }}>
            Avance horas ({formatHours(latestData.horas)} / 320h)
          </p>
          <div className="admin-prog-bar bg-white mt-2 mx-auto" style={{ width: '100%' }}>
            <span
              className="admin-prog-fill"
              style={{
                width: `${Math.min(horasAvancePct, 100)}%`,
                background: horasAvancePct >= 70 ? 'var(--admin-green)' : horasAvancePct >= 40 ? '#e09b00' : 'var(--admin-red)',
              }}
            />
          </div>
        </div>

        <div className="admin-detail-field" style={{ textAlign: 'center' }}>
          <Award className="w-5 h-5 mx-auto mb-1" style={{ color: 'var(--admin-green)' }} />
          <p className="text-[22px] font-bold" style={{ color: cumplColor }}>
            {latestData.cumplimiento.toFixed(2)}%
          </p>
          <p className="text-[11px]" style={{ color: 'var(--admin-text3)' }}>
            Cumplimiento General
          </p>
          {cambioCumplimiento !== 0 && (
            <p className="text-[10px] mt-0.5" style={{ color: cambioCumplimiento > 0 ? 'var(--admin-green)' : 'var(--admin-red)' }}>
              {cambioCumplimiento > 0 ? '↑' : '↓'} {Math.abs(cambioCumplimiento).toFixed(1)}% vs inicio
            </p>
          )}
        </div>

        {empresaData && (
          <>
            {[
              { label: empresaData.resumen.nombreN1 ?? 'Norma 1', value: empresaData.resumen.cumplimientoN1 },
              { label: empresaData.resumen.nombreN2 ?? 'Norma 2', value: empresaData.resumen.cumplimientoN2 },
              { label: empresaData.resumen.nombreN3 ?? 'Norma 3', value: empresaData.resumen.cumplimientoN3 },
            ].map((ntc) => {
              const ntcColor = ntc.value >= 70 ? 'var(--admin-green)' : ntc.value >= 50 ? '#e09b00' : 'var(--admin-red)';
              return (
                <div key={ntc.label} className="admin-detail-field" style={{ textAlign: 'center' }}>
                  <p className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: 'var(--admin-text3)' }}>
                    {ntc.label}
                  </p>
                  <p className="text-[20px] font-bold" style={{ color: ntcColor }}>
                    {ntc.value.toFixed(2)}%
                  </p>
                  <div className="admin-prog-bar bg-white mt-1.5 mx-auto" style={{ width: '100%' }}>
                    <span className="admin-prog-fill" style={{ width: `${Math.min(ntc.value, 100)}%`, background: ntcColor }} />
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Chart mode toggle */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold text-(--admin-text3) uppercase tracking-wider mr-1">Vista:</span>
        <button
          onClick={() => setChartMode('separate')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${
            chartMode === 'separate'
              ? 'bg-(--admin-blue-bg) text-(--admin-blue) border-(--admin-blue)'
              : 'bg-(--admin-surface2) text-(--admin-text2) border-(--admin-border) hover:border-(--admin-blue)'
          }`}
        >
          <BarChart2 className="w-3.5 h-3.5" />
          Gráficas separadas
        </button>
        <button
          onClick={() => setChartMode('combined')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${
            chartMode === 'combined'
              ? 'bg-(--admin-blue-bg) text-(--admin-blue) border-(--admin-blue)'
              : 'bg-(--admin-surface2) text-(--admin-text2) border-(--admin-border) hover:border-(--admin-blue)'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          Todo en una gráfica
        </button>
      </div>

      {/* ── COMBINED view ── */}
      {chartMode === 'combined' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Evolución combinada — clic en un punto para ir a la asesoría
            </CardTitle>
          </CardHeader>
          <CardBody>
            <ResponsiveContainer width="100%" height={420}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '11px' }} />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '11px' }}
                  domain={[0, 100]}
                  label={{ value: '%', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<SimpleTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="cumplimiento"
                  stroke="#0d9488"
                  strokeWidth={3}
                  name="% Cumplimiento global"
                  dot={{ fill: '#0d9488', r: 4 }}
                  activeDot={makeDot(chartData, navigate, nit, '#0d9488', 6)}
                />
                {ntcKeys.map((ntc) => (
                  <Line
                    key={ntc.key}
                    type="monotone"
                    dataKey={ntc.key}
                    stroke={ntc.color}
                    strokeWidth={2}
                    strokeDasharray="4 2"
                    name={ntc.label}
                    dot={{ fill: ntc.color, r: 3 }}
                    activeDot={makeDot(chartData, navigate, nit, ntc.color, 4)}
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="horas"
                  stroke="#2563eb"
                  strokeWidth={2}
                  name="Horas acumuladas"
                  dot={{ fill: '#2563eb', r: 3 }}
                  activeDot={makeDot(chartData, navigate, nit, '#2563eb', 4)}
                />
              </LineChart>
            </ResponsiveContainer>
            <p className="mt-3 text-[11px] text-(--admin-text3) italic">
              Línea sólida teal = cumplimiento global · líneas punteadas = por norma · línea azul = horas acumuladas (misma escala)
            </p>
          </CardBody>
        </Card>
      )}

      {/* ── SEPARATE view ── */}
      {chartMode === 'separate' && (
        <>
          {/* Cumplimiento */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                Evolución del Cumplimiento
              </CardTitle>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={380}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '12px' }} />
                  <YAxis
                    stroke="#6b7280"
                    style={{ fontSize: '12px' }}
                    domain={[0, 100]}
                    label={{ value: '%', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<SimpleTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="cumplimiento"
                    stroke="#0d9488"
                    strokeWidth={3}
                    name="% Cumplimiento"
                    dot={{ fill: '#0d9488', r: 4 }}
                    activeDot={makeDot(chartData, navigate, nit, '#0d9488')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          {/* Horas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Evolución de Horas de Asesoría
              </CardTitle>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={380}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '12px' }} />
                  <YAxis
                    stroke="#6b7280"
                    style={{ fontSize: '12px' }}
                    domain={[0, 320]}
                    label={{ value: 'Horas', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<SimpleTooltip isHoras />} />
                  <Legend />
                  <ReferenceLine y={320} stroke="#ef4444" strokeDasharray="3 3" label="Meta Anual (320h)" />
                  <Line
                    type="monotone"
                    dataKey="horas"
                    stroke="#2563eb"
                    strokeWidth={3}
                    name="Horas de Asesoría"
                    dot={{ fill: '#2563eb', r: 4 }}
                    activeDot={makeDot(chartData, navigate, nit, '#2563eb')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          {/* Per-NTC small multiples */}
          {ntcKeys.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {ntcKeys.map((ntc) => (
                <Card key={ntc.key}>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-(--admin-blue)" />
                        Avance {ntc.label}
                      </div>
                      <button
                        onClick={() => navigate(`/normas/${ntc.ntcId}`)}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                        title="Ver detalle de norma"
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '10px' }} />
                        <YAxis stroke="#6b7280" style={{ fontSize: '10px' }} domain={[0, 100]} />
                        <Tooltip content={<SimpleTooltip />} />
                        <Line
                          type="monotone"
                          dataKey={ntc.key}
                          stroke={ntc.color}
                          strokeWidth={2}
                          name="% Cumplimiento"
                          dot={{ fill: ntc.color, r: 3 }}
                          activeDot={makeDot(chartData, navigate, nit, ntc.color, 4)}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Historical table */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico Detallado</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Período</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">% Cumplimiento</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Horas Asesoría</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Fecha Registro</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {chartData.map((item, index) => {
                  const original = historial[index];
                  return (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50 ${item.asesoriaId ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        if (item.asesoriaId) navigate(`/empresas/${nit}/asesorias/${item.asesoriaId}`);
                      }}
                    >
                      <td className="px-4 py-3 text-gray-900 font-medium">{item.name}</td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`font-medium ${
                            item.cumplimiento >= 80 ? 'text-green-600' : item.cumplimiento >= 50 ? 'text-yellow-600' : 'text-red-600'
                          }`}
                        >
                          {item.cumplimiento.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-medium text-blue-600">{formatHours(item.horas)}</td>
                      <td className="px-4 py-3 text-center text-gray-600 text-xs">
                        {new Date(original.fechaRegistro).toLocaleDateString('es-CO', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.asesoriaId && <ExternalLink className="w-3.5 h-3.5 text-gray-400 mx-auto" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
